import { useEffect, useMemo, useState } from "react";
import { claimToken, readSessionCodeFromUrl } from "./session/token";
import {
  finishSession,
  getSession,
  submitAnswer,
  type RunnerSession,
} from "./session/runnerApi";
import { SessionPlayer, type PlayerApi } from "./session/SessionPlayer";

/**
 * Uretim girisi.
 *
 * Akis: URL'den session kodu -> fragment'tan token -> URL temizligi ->
 * GET /api/v4/runner/session -> SessionPlayer. Player cevaplari
 * POST .../items/{id}/answer, tamamlaninca POST .../finish ile gonderir.
 */

type BootState =
  | { kind: "loading" }
  | { kind: "error"; message: string }
  | { kind: "ready"; token: string; session: RunnerSession };

export function App() {
  const [state, setState] = useState<BootState>({ kind: "loading" });

  useEffect(() => {
    const sessionCode = readSessionCodeFromUrl(window.location.pathname);

    if (!sessionCode) {
      setState({
        kind: "error",
        message:
          "Geçersiz adres. Uygulamayı öğretmen panelinden başlatmanız gerekiyor.",
      });
      return;
    }

    const token = claimToken(sessionCode);
    if (!token) {
      setState({
        kind: "error",
        message:
          "Oturum bilgisi bulunamadı. Uygulamayı öğretmen panelinden yeniden başlatın.",
      });
      return;
    }

    let cancelled = false;
    const ctrl = new AbortController();
    getSession(token, ctrl.signal)
      .then((session) => {
        if (!cancelled) setState({ kind: "ready", token, session });
      })
      .catch((e) => {
        if (cancelled) return;
        setState({
          kind: "error",
          message: e instanceof Error ? e.message : "Oturum yüklenemedi.",
        });
      });

    return () => {
      cancelled = true;
      ctrl.abort();
    };
  }, []);

  const api = useMemo<PlayerApi | null>(() => {
    if (state.kind !== "ready") return null;
    const token = state.token;
    return {
      submitAnswer: (itemId, body) =>
        submitAnswer(token, itemId, body).then(() => undefined),
      finish: () => finishSession(token).then(() => undefined),
    };
  }, [state]);

  if (state.kind === "loading") {
    return <main className="screen">Yükleniyor…</main>;
  }

  if (state.kind === "error") {
    return (
      <main className="screen">
        <p className="message">{state.message}</p>
      </main>
    );
  }

  return <SessionPlayer session={state.session} api={api!} />;
}
